import Config from '@config';
import { normalizeInfiniteQueryData } from '@gasbi/shared/common/features/utils';
import { createApi } from '@reduxjs/toolkit/query/react';

import { CACHE_EXPIRATION_TIME } from '@/features/constants';
import { FiltersGrid, Pagination } from '@/features/types';
import { infiniteQueryOptions } from '@/features/utils';
import axiosBaseQuery from '@/utils/axiosBaseQuery';

import { GasSupplier, GasSupplierRequestBody } from './types';

export const gasSuppliersApi = createApi({
  reducerPath: 'gasSuppliersApi',
  baseQuery: axiosBaseQuery({
    baseUrl: `${Config.VITE_API_URL}/gas-suppliers`,
  }),
  tagTypes: ['GasSupplier', 'GasSuppliers'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getGasSuppliersI: builder.infiniteQuery<
      GasSupplier[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: `/query`,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['GasSupplier', 'GasSuppliers'],
    }),
    getGasSupplierById: builder.query<GasSupplier, string>({
      query: id => ({
        url: `/${id}`,
      }),
      providesTags: () => ['GasSupplier'],
    }),
    createGasSupplier: builder.mutation<GasSupplier, GasSupplierRequestBody>({
      query: data => ({
        url: '/',
        method: 'POST',
        data,
      }),
      invalidatesTags: result => [
        'GasSupplier',
        { type: 'GasSupplier', id: result?.id },
      ],
    }),
    updateGasSupplier: builder.mutation<GasSupplier, GasSupplierRequestBody>({
      query: data => ({
        url: `/${data.id}`,
        method: 'PUT',
        data,
      }),
      invalidatesTags: result => [
        'GasSupplier',
        { type: 'GasSupplier', id: result?.id },
      ],
    }),
    deleteGasSupplierById: builder.mutation<void, string>({
      query: id => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: (_, __, id) => [
        'GasSupplier',
        { type: 'GasSupplier', id },
      ],
    }),
  }),
});

export const {
  useGetGasSupplierByIdQuery,
  useCreateGasSupplierMutation,
  useUpdateGasSupplierMutation,
} = gasSuppliersApi;

export const useGetGasSuppliersIInfiniteQuery = normalizeInfiniteQueryData(
  gasSuppliersApi.useGetGasSuppliersIInfiniteQuery,
);
